<!-- app-subdomain-creators\src\Components\MemberDetailsProfile.vue -->

<template>
  <div
    v-if="member"
    class="grid max-w-7xl grid-cols-1 px-4 sm:px-6 lg:px-8"
  >
    <form
      class="md:col-span-2"
      @submit.prevent="saveProfile"
    >
      <div class="grid grid-cols-1 gap-x-5 gap-y-6 sm:max-w-xl">
        <div class="col-span-full flex items-center gap-x-8">
          <div class="relative inline-block group">
            <img
              :src="member.profilePic || defaultProfilePic"
              alt="User Avatar"
              class="h-24 w-24 flex-none rounded-full bg-gray-800 object-cover shadow-xl"
            >
            <button
              type="button"
              class="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-all duration-200 group-hover:opacity-100 disabled:cursor-not-allowed"
              :disabled="isUploadingAvatar"
              @click="triggerAvatarPicker"
            >
              <CameraIcon class="h-7 w-7" />
            </button>
            <span
              :class="statusClass"
              class="absolute right-2 bottom-2 block h-4 w-4 rounded-full ring-4 ring-white"
            />
            <div class="absolute hidden group-hover:block -top-8 text-center -right-24 bg-black bg-opacity-60 text-white text-sm rounded-md px-4 py-2 w-36">
              {{ statusTooltip }}
            </div>
            <input
              ref="avatarInputRef"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleAvatarSelected"
            >
          </div>

          <div>
            <div class="flex items-center gap-2">
              <h3 class="text-[30px]">
                {{ member.name }}
              </h3>
            </div>
            <div class="flex items-center gap-2 relative">
              <h3 class="text-[15px]">
                {{ member.email }}
              </h3>
              <div class="relative inline-flex items-center">
                <ClipboardIcon
                  class="h-5 text-zinc-400 cursor-pointer"
                  @click="copyToClipboard(member.email, 'email')"
                />
                <span
                  v-if="copiedField === 'email'"
                  class="absolute left-full ml-2 top-1/2 -translate-y-1/2 text-green-500 text-xs whitespace-nowrap transition-opacity duration-500"
                >
                  Copied!
                </span>
              </div>
            </div>
            <div class="mt-2 flex items-center gap-2">
              <span
                :class="badgeClass(member.subscriptionStatus)"
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              >
                {{ member.subscriptionStatus }}
              </span>
              <span
                :class="badgeClassPlans(member.subscriptionPlan)"
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              >
                {{ displaySubscriptionPlan(member.subscriptionPlan) }}
              </span>
            </div>
            <p
              v-if="isUploadingAvatar"
              class="mt-2 text-xs font-medium text-zinc-500"
            >
              Uploading avatar...
            </p>
          </div>

          <div class="relative user-dropdown w-11 h-11 p-2 -mt-16 hover:bg-zinc-200 bg-zinc-100 rounded-full items-center">
            <EllipsisVerticalIcon
              class="text-zinc-500 h-7 cursor-pointer"
              @click.stop="toggleUserMenu"
            />

            <div
              v-if="menuOpen"
              class="absolute z-10 -mt-5 -right-44 w-40 bg-white border border-zinc-200 rounded-md shadow-lg"
            >
              <ul>
                <li>
                  <button
                    class="block w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 font-normal"
                    @click.stop="openEditMode"
                  >
                    Edit Member Details
                  </button>
                </li>
                <li>
                  <button
                    class="block w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 font-normal"
                    @click.stop="handleUserAction('delete')"
                  >
                    Delete Member
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          v-if="assignedTags.length"
          class="flex flex-wrap gap-2"
        >
          <span
            v-for="tag in assignedTags"
            :key="`member-tag-${tag.userTagId || tag.tagId || tag.name}`"
            class="inline-flex items-center rounded-md bg-blue-50 px-4 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
          >
            {{ tag.name }}
          </span>
        </div>

        <div
          v-if="isEditing"
          class="grid grid-cols-1 gap-y-4 ml-7"
        >
          <div class="flex gap-4 justify-between">
            <div class="sm:col-span-3 w-full">
              <label
                for="first_name"
                class="block text-sm font-medium leading-6 text-zinc-600"
              >First name</label>
              <div class="mt-1">
                <input
                  id="first_name"
                  v-model="form.firstName"
                  type="text"
                  name="first_name"
                  autocomplete="given-name"
                  class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
                >
              </div>
            </div>

            <div class="sm:col-span-3 w-full">
              <label
                for="last_name"
                class="block text-sm font-medium leading-6 text-zinc-600"
              >Last name</label>
              <div class="mt-1">
                <input
                  id="last_name"
                  v-model="form.lastName"
                  type="text"
                  name="last_name"
                  autocomplete="family-name"
                  class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
                >
              </div>
            </div>
          </div>

          <div class="col-span-full">
            <label
              for="email"
              class="block text-sm font-medium leading-6 text-zinc-600"
            >Email address</label>
            <div class="mt-1">
              <input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                autocomplete="email"
                class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
              >
            </div>
          </div>

          <div class="flex gap-4 justify-between">
            <div class="sm:col-span-3 w-full">
              <div class="flex items-center gap-1">
                <label
                  for="alias"
                  class="block text-sm font-medium leading-6 text-zinc-600"
                >Alias</label>
                <div class="relative group inline-flex items-center">
                  <InformationCircleIcon class="h-4 w-4 text-zinc-400" />
                  <p class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0">
                    The name other members will see the member as.
                  </p>
                </div>
              </div>
              <div class="mt-1">
                <input
                  id="alias"
                  v-model="form.alias"
                  type="text"
                  name="alias"
                  autocomplete="off"
                  class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
                >
              </div>
            </div>

            <div class="sm:col-span-3 w-full">
              <div class="flex items-center gap-1">
                <label
                  for="handle"
                  class="block text-sm font-medium leading-6 text-zinc-600"
                >Handle</label>
                <div class="relative group inline-flex items-center">
                  <InformationCircleIcon class="h-4 w-4 text-zinc-400" />
                  <p class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0">
                    Handle is used to call out the member throughout the app.
                  </p>
                </div>
              </div>
              <div class="mt-1">
                <div class="flex rounded-md bg-white/10 ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-[#2D8CFF]">
                  <span class="flex select-none items-center pl-3 text-zinc-500 sm:text-sm">@</span>
                  <input
                    id="handle"
                    v-model="handleInputValue"
                    type="text"
                    name="handle"
                    autocomplete="off"
                    placeholder="member"
                    class="flex-1 border-0 bg-transparent py-1.5 pl-1 text-zinc-600 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="col-span-full">
            <label
              for="phone"
              class="block text-sm font-medium leading-6 text-zinc-600"
            >Phone number</label>
            <div class="mt-1">
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                name="phone"
                autocomplete="tel"
                class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
              >
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 rounded-md border border-zinc-200 bg-zinc-50 p-3">
            <div>
              <p class="text-sm font-semibold text-zinc-700">
                Stripe customer IDs
              </p>
              <p class="mt-1 text-xs text-zinc-500">
                Optional manual maintenance fields for payment recovery and account matching.
              </p>
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  for="connected_creator_stripe_customer_id"
                  class="block text-sm font-medium leading-6 text-zinc-600"
                >Connected Creator Stripe cus_id</label>
                <div class="mt-1">
                  <input
                    id="connected_creator_stripe_customer_id"
                    v-model.trim="form.connectedCreatorStripeCustomerId"
                    type="text"
                    name="connected_creator_stripe_customer_id"
                    autocomplete="off"
                    placeholder="cus_..."
                    class="block w-full rounded-md border-0 bg-white py-1.5 font-mono text-xs text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-zinc-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF]"
                  >
                </div>
              </div>

              <div>
                <label
                  for="platforma_payments_stripe_customer_id"
                  class="block text-sm font-medium leading-6 text-zinc-600"
                >Platforma Payments Stripe cus_id</label>
                <div class="mt-1">
                  <input
                    id="platforma_payments_stripe_customer_id"
                    v-model.trim="form.platformaPaymentsStripeCustomerId"
                    type="text"
                    name="platforma_payments_stripe_customer_id"
                    autocomplete="off"
                    placeholder="cus_..."
                    class="block w-full rounded-md border-0 bg-white py-1.5 font-mono text-xs text-zinc-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-zinc-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF]"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8 flex ml-6">
        <div class="flex gap-2 items-center">
          <div
            v-if="successMessage"
            class="ml-2 flex gap-1 items-center transition-opacity duration-500 ease-in-out"
          >
            <CheckCircleIcon class="h-7 text-green-500" />
            <p class="text-green-500 text-md font-normal">
              {{ successMessageText }}
            </p>
          </div>
        </div>
        <div class="flex gap-2">
          <button
            v-if="isEditing"
            type="submit"
            class="rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            :class="{
              'bg-gray-300 hover:bg-gray-400 cursor-not-allowed': disableSave,
              'bg-green-500 hover:bg-green-400': !disableSave
            }"
          >
            Save
          </button>
          <button
            v-if="isEditing"
            type="button"
            class="rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-300"
            :class="{
              'bg-green-500 hover:bg-green-400': disableSave,
              'bg-zinc-300 hover:bg-zinc-400': !disableSave
            }"
            @click="cancelEdit"
          >
            Cancel
          </button>
        </div>
      </div>
      <div
        v-if="errorMessage"
        class="mt-3 ml-6 flex gap-2 items-center"
      >
        <ExclamationTriangleIcon class="text-red-400 h-7" />
        <p class="text-red-400 font-semibold">
          {{ errorMessage }}
        </p>
      </div>
    </form>

    <div
      v-if="member"
      class="mt-8 space-y-7"
    >
      <div>
        <div class="mt-1">
          <div
            v-if="!showPasswordResetConfirm"
            class="flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="passwordResetSending || creatorAccessLaunching || !member.email"
              @click="openPasswordResetConfirm"
            >
              <EnvelopeIcon class="h-5 w-5" />
              Send Password Reset Email
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="passwordResetSending || creatorAccessLaunching || !member.email"
              @click="openMemberAccountAsCreator"
            >
              <ArrowRightCircleIcon class="h-5 w-5" />
              {{ creatorAccessLaunching ? 'Opening...' : 'Open Member Account' }}
            </button>
          </div>
          <div
            v-else
            class="max-w-xl rounded-md border border-zinc-200 bg-zinc-50 p-3"
          >
            <p class="text-xs text-zinc-700">
              Are you sure you want to send a password reset email to <span class="font-semibold">{{ member.email }}</span>?
            </p>
            <label class="mt-3 flex items-start gap-2 text-xs text-zinc-700">
              <input
                v-model="passwordResetConfirmChecked"
                type="checkbox"
                class="mt-0.5 h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-400"
              >
              <span>I understand this sends a secure reset link to the member email on file.</span>
            </label>
            <div class="mt-3 flex items-center gap-2">
              <button
                type="button"
                class="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!passwordResetConfirmChecked || passwordResetSending"
                @click="sendPasswordResetEmail"
              >
                {{ passwordResetSending ? 'Sending...' : 'Confirm & Send' }}
              </button>
              <button
                type="button"
                class="rounded-md bg-zinc-300 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-zinc-400"
                :disabled="passwordResetSending"
                @click="cancelPasswordResetConfirm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="passwordResetSent"
          class="mt-2 flex items-center gap-1 text-xs text-green-500"
        >
          <CheckCircleIcon class="h-4 w-4" />
          <p>Password reset email sent.</p>
        </div>
        <p
          v-if="passwordResetError"
          class="mt-2 text-xs text-red-500"
        >
          {{ passwordResetError }}
        </p>
        <p
          v-if="creatorAccessError"
          class="mt-2 text-xs text-red-500"
        >
          {{ creatorAccessError }}
        </p>
      </div>

      <div>
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-zinc-900">
            Associated Emails
          </h3>
        </div>
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <span
            v-for="email in associatedEmails"
            :key="`associated-email-${email}`"
            class="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/20"
          >
            {{ email }}
          </span>
          <button
            type="button"
            class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-zinc-700 transition-colors hover:bg-zinc-200"
            :disabled="associatedEmailSaving"
            @click="openAssociatedEmailInput"
          >
            <PlusIcon class="h-4 w-4" />
            <span class="sr-only">Add associated email</span>
          </button>
        </div>
        <p
          v-if="!associatedEmails.length"
          class="mt-2 text-xs text-zinc-500"
        >
          No associated emails yet.
        </p>
        <div
          v-if="isAddingAssociatedEmail"
          class="mt-3 flex flex-wrap items-center gap-2"
        >
          <input
            v-model="newAssociatedEmail"
            type="email"
            placeholder="Add email"
            class="block w-full max-w-xs rounded-md border-0 py-1.5 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-zinc-400 sm:text-sm sm:leading-6"
            @keydown.enter.prevent="saveAssociatedEmail"
          >
          <button
            type="button"
            class="rounded-md bg-green-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-green-400"
            :disabled="associatedEmailSaving"
            @click="saveAssociatedEmail"
          >
            {{ associatedEmailSaving ? 'Saving...' : 'Add email' }}
          </button>
          <button
            type="button"
            class="rounded-md bg-zinc-300 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-zinc-400"
            :disabled="associatedEmailSaving"
            @click="cancelAssociatedEmailInput"
          >
            Cancel
          </button>
        </div>
        <div
          v-if="associatedEmailSaved"
          class="mt-2 flex items-center gap-1 text-xs text-green-500"
        >
          <CheckCircleIcon class="h-4 w-4" />
          <p>Saved.</p>
        </div>
        <p
          v-if="associatedEmailError"
          class="mt-2 text-xs text-red-500"
        >
          {{ associatedEmailError }}
        </p>
      </div>

      <div>
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-zinc-900">
            Profile Change History
          </h3>
          <button
            type="button"
            class="inline-flex items-center rounded-md bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-200"
            :disabled="historyLoading"
            @click="fetchProfileHistory(profileHistoryPagination.page)"
          >
            {{ historyLoading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>

        <div class="mt-4 overflow-hidden rounded-xl border border-zinc-200">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-zinc-200">
              <thead class="bg-zinc-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Date
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Changed By
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Field
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Previous
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Changed To
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-zinc-200 bg-white">
                <tr
                  v-for="entry in profileHistoryRows"
                  :key="`profile-history-${entry.id}`"
                >
                  <td class="whitespace-nowrap px-4 py-3 text-sm text-zinc-700">
                    {{ formatHistoryDate(entry.changedAt) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-zinc-700">
                    {{ entry.actorDisplayName || 'System' }}
                  </td>
                  <td class="px-4 py-3 text-sm text-zinc-700">
                    {{ entry.fieldLabel }}
                  </td>
                  <td class="max-w-[280px] truncate px-4 py-3 text-sm text-zinc-500">
                    {{ entry.previousValue || '-' }}
                  </td>
                  <td class="max-w-[320px] truncate px-4 py-3 text-sm text-zinc-900">
                    {{ entry.nextValue || '-' }}
                  </td>
                </tr>
                <tr v-if="!historyLoading && !profileHistoryRows.length">
                  <td
                    colspan="5"
                    class="px-4 py-8 text-center text-sm text-zinc-500"
                  >
                    No profile changes recorded yet.
                  </td>
                </tr>
                <tr v-if="historyLoading && !profileHistoryRows.length">
                  <td
                    colspan="5"
                    class="px-4 py-8 text-center text-sm text-zinc-500"
                  >
                    Loading profile history...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="mt-4 flex items-center justify-between text-sm text-zinc-600">
          <div>
            Showing {{ profileHistoryRows.length ? historyStartRow : 0 }} to {{ historyEndRow }} of {{ profileHistoryPagination.totalCount }}
          </div>
          <div class="flex items-center gap-2">
            <label
              for="profile-history-page-size"
              class="text-sm text-zinc-600"
            >Rows per page:</label>
            <PlatformaSelect
              id="profile-history-page-size"
              v-model.number="profileHistoryPagination.pageSize"
              class="min-w-[110px]"
              :options="pageSizeSelectOptions"
              :disabled="historyLoading"
              menu-placement="top"
              @update:model-value="fetchProfileHistory(1)"
            />
            <button
              type="button"
              class="rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!profileHistoryPagination.hasPreviousPage || historyLoading"
              @click="fetchProfileHistory(profileHistoryPagination.page - 1)"
            >
              Previous
            </button>
            <button
              type="button"
              class="rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!profileHistoryPagination.hasNextPage || historyLoading"
              @click="fetchProfileHistory(profileHistoryPagination.page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, onBeforeUnmount } from 'vue';
import {
  CameraIcon,
  CheckCircleIcon,
  ClipboardIcon,
  ExclamationTriangleIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  PlusIcon,
  ArrowRightCircleIcon,
} from '@heroicons/vue/24/outline';
import { useWebsocketStore } from '@/stores/websocketStore';
import { usePresenceStore } from '@/stores/presenceStore';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const websocketStore = useWebsocketStore();
const presenceStore = usePresenceStore();
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  memberId: {
    type: String,
    required: true,
  },
});

const member = ref(null);
const assignedTags = ref([]);
const associatedEmails = ref([]);
const isAddingAssociatedEmail = ref(false);
const newAssociatedEmail = ref('');
const associatedEmailSaving = ref(false);
const associatedEmailError = ref('');
const associatedEmailSaved = ref(false);
const showPasswordResetConfirm = ref(false);
const passwordResetConfirmChecked = ref(false);
const passwordResetSending = ref(false);
const passwordResetError = ref('');
const passwordResetSent = ref(false);
const creatorAccessLaunching = ref(false);
const creatorAccessError = ref('');
const profileHistoryRows = ref([]);
const historyLoading = ref(false);
const profileHistoryPagination = ref({
  page: 1,
  pageSize: 25,
  totalCount: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false,
});
const pageSizeSelectOptions = Object.freeze([
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
]);
const errorMessage = ref('');
const saving = ref(false);
const isUploadingAvatar = ref(false);
const avatarInputRef = ref(null);
const successMessage = ref(false);
const successMessageText = ref('Profile changes saved successfully!');
const copiedField = ref(null);
const isEditing = ref(false);
const menuOpen = ref(false);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaultProfilePic = require('@/assets/media/images/default-avatar.png');
const DEFAULT_MEMBER_PROFILE_PLACEHOLDER = 'src/assets/media/images/default-avatar.png';
const isInlineDataUri = (value) => /^data:/i.test(String(value || '').trim());
const normalizeProfilePicForSave = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  if (normalized === defaultProfilePic) return '';
  if (normalized === DEFAULT_MEMBER_PROFILE_PLACEHOLDER) return '';
  if (isInlineDataUri(normalized)) return '';
  return normalized;
};

const form = ref({
  firstName: '',
  lastName: '',
  alias: '',
  handle: '',
  email: '',
  phone: '',
  connectedCreatorStripeCustomerId: '',
  platformaPaymentsStripeCustomerId: '',
  profilePicUrl: '',
});

const disableSave = computed(() => !!errorMessage.value || saving.value);
const historyStartRow = computed(() => {
  const page = Number(profileHistoryPagination.value.page || 1);
  const pageSize = Number(profileHistoryPagination.value.pageSize || 25);
  const total = Number(profileHistoryPagination.value.totalCount || 0);
  if (!total) return 0;
  return ((page - 1) * pageSize) + 1;
});
const historyEndRow = computed(() => {
  const page = Number(profileHistoryPagination.value.page || 1);
  const pageSize = Number(profileHistoryPagination.value.pageSize || 25);
  const total = Number(profileHistoryPagination.value.totalCount || 0);
  if (!total) return 0;
  return Math.min(total, page * pageSize);
});

const memberPresenceStatus = computed(() => {
  const id = member.value?.userId ?? member.value?.user_id ?? null;
  if (!id) return 'offline';
  return presenceStore.memberStatus(id);
});

const statusClass = computed(() => {
  switch (memberPresenceStatus.value) {
    case 'online':
      return 'bg-green-400';
    case 'away':
      return 'bg-yellow-400';
    case 'offline':
    default:
      return 'bg-gray-400';
  }
});

const statusTooltip = computed(() => {
  switch (memberPresenceStatus.value) {
    case 'online':
      return 'Member Online';
    case 'away':
      return 'Member Away';
    case 'offline':
      return 'Member Offline';
    default:
      return 'Status unknown';
  }
});

const badgeClass = (status) => {
  switch (status) {
    case 'Cancelled':
      return 'bg-red-50 text-red-700 ring-red-600/20';
    case 'Active':
      return 'bg-blue-50 text-blue-700 ring-blue-600/20';
    case 'Trialing':
      return 'bg-sky-50 text-sky-700 ring-sky-600/20';
    case 'Paused':
      return 'bg-zinc-100 text-zinc-700 ring-zinc-400/20';
    case 'Subscribed':
      return 'bg-green-50 text-green-700 ring-green-600/20';
    case 'Cancelling':
      return 'bg-red-50 text-red-700 ring-red-600/20';
    case 'Waiting for Payment':
      return 'bg-amber-50 text-amber-700 ring-amber-600/20';
    case 'Failed Payment':
      return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
    case 'No Plan':
      return 'bg-zinc-100 text-zinc-600 ring-zinc-500/20';
    case 'Custom Subscription':
      return 'bg-purple-50 text-purple-700 ring-purple-600/20';
    default:
      return 'bg-green-50 text-green-700 ring-green-600/20';
  }
};

const isCustomPlanLabel = (value) => String(value || '').trim().toLowerCase().includes('custom');

const displaySubscriptionPlan = (value) => {
  if (isCustomPlanLabel(value)) return 'Custom';
  return String(value || '').trim() || 'Basic';
};

const badgeClassPlans = (planName) => {
  if (isCustomPlanLabel(planName)) {
    return 'bg-purple-50 text-purple-700 ring-purple-600/20';
  }
  switch (String(planName || '').trim()) {
    case 'Free Pass':
      return 'bg-blue-50 text-blue-700 ring-blue-600/20';
    default:
      return 'bg-green-50 text-green-700 ring-green-600/20';
  }
};

const handleInputValue = computed({
  get() {
    const raw = String(form.value.handle || '').trim();
    return raw.startsWith('@') ? raw.slice(1) : raw;
  },
  set(value) {
    const normalized = String(value || '').replace(/^@+/, '').replace(/\s+/g, '');
    form.value.handle = normalized ? `@${normalized}` : '@';
  },
});

const showSuccess = (message = 'Profile changes saved successfully!') => {
  successMessageText.value = message;
  successMessage.value = true;
  setTimeout(() => {
    successMessage.value = false;
  }, 3000);
};

const openAssociatedEmailInput = () => {
  isAddingAssociatedEmail.value = true;
  associatedEmailSaved.value = false;
  associatedEmailError.value = '';
};

const cancelAssociatedEmailInput = () => {
  isAddingAssociatedEmail.value = false;
  newAssociatedEmail.value = '';
  associatedEmailSaving.value = false;
  associatedEmailError.value = '';
  associatedEmailSaved.value = false;
};

const saveAssociatedEmail = async () => {
  if (!member.value?.userId) {
    associatedEmailError.value = 'Member is not loaded yet.';
    return;
  }

  const email = String(newAssociatedEmail.value || '').trim().toLowerCase();
  if (!email || !EMAIL_REGEX.test(email)) {
    associatedEmailError.value = 'Please enter a valid email address.';
    return;
  }
  if (associatedEmails.value.includes(email)) {
    associatedEmailError.value = 'This email is already associated with this member.';
    return;
  }

  associatedEmailSaving.value = true;
  associatedEmailError.value = '';
  associatedEmailSaved.value = false;
  try {
    const response = await fetch(`${apiProxyUrl}/api/members/${member.value.userId}/associated-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.success) {
      throw new Error(String(payload?.message || 'Failed to add associated email.'));
    }

    if (Array.isArray(payload?.associatedEmails)) {
      associatedEmails.value = payload.associatedEmails
        .map((entry) => String(entry || '').trim())
        .filter(Boolean);
    } else {
      await fetchProfileHistory(profileHistoryPagination.value.page || 1);
    }

    newAssociatedEmail.value = '';
    isAddingAssociatedEmail.value = false;
    associatedEmailSaved.value = true;
    setTimeout(() => {
      associatedEmailSaved.value = false;
    }, 2500);
  } catch (error) {
    associatedEmailError.value = String(error?.message || 'Unable to save associated email right now.');
  } finally {
    associatedEmailSaving.value = false;
  }
};

const openPasswordResetConfirm = () => {
  showPasswordResetConfirm.value = true;
  passwordResetConfirmChecked.value = false;
  passwordResetError.value = '';
  creatorAccessError.value = '';
  passwordResetSent.value = false;
};

const cancelPasswordResetConfirm = () => {
  showPasswordResetConfirm.value = false;
  passwordResetConfirmChecked.value = false;
  passwordResetSending.value = false;
};

const sendPasswordResetEmail = async () => {
  if (!member.value?.userId) {
    passwordResetError.value = 'Member is not loaded yet.';
    return;
  }
  passwordResetError.value = '';
  creatorAccessError.value = '';
  passwordResetSent.value = false;
  passwordResetSending.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/members/${member.value.userId}/password-reset-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to send password reset email.'));
    }

    showPasswordResetConfirm.value = false;
    passwordResetConfirmChecked.value = false;
    passwordResetSent.value = true;
    setTimeout(() => {
      passwordResetSent.value = false;
    }, 3000);
  } catch (error) {
    passwordResetError.value = String(error?.message || 'Unable to send password reset email right now.');
  } finally {
    passwordResetSending.value = false;
  }
};

const openMemberAccountAsCreator = async () => {
  if (!member.value?.userId) {
    creatorAccessError.value = 'Member is not loaded yet.';
    return;
  }
  const pendingWindow = typeof window !== 'undefined'
    ? window.open('about:blank', '_blank')
    : null;
  if (pendingWindow && !pendingWindow.closed) {
    try {
      pendingWindow.document.title = 'Opening member account...';
      pendingWindow.document.body.innerHTML = '<p style="font-family:Arial,sans-serif;padding:16px;">Opening member account...</p>';
    } catch (_ignore) {
      // Non-blocking: continue with navigation flow.
    }
  }
  creatorAccessError.value = '';
  passwordResetError.value = '';
  creatorAccessLaunching.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/members/${member.value.userId}/creator-access-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to open member account.'));
    }

    const launchUrl = String(payload?.launchUrl || '').trim();
    if (!launchUrl) {
      throw new Error('Missing member access link.');
    }
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.location.href = launchUrl;
      try {
        pendingWindow.opener = null;
      } catch (_ignore) {
        // Non-blocking: opener hardening only.
      }
      return;
    }
    if (typeof window !== 'undefined') {
      const openedWindow = window.open(launchUrl, '_blank');
      if (openedWindow) {
        try {
          openedWindow.opener = null;
        } catch (_ignore) {
          // Non-blocking: opener hardening only.
        }
      } else {
        window.location.assign(launchUrl);
      }
    }
  } catch (error) {
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.close();
    }
    creatorAccessError.value = String(error?.message || 'Unable to open member account right now.');
  } finally {
    creatorAccessLaunching.value = false;
  }
};

const copyToClipboard = (text, field) => {
  navigator.clipboard.writeText(text).then(() => {
    copiedField.value = field;
    setTimeout(() => {
      copiedField.value = null;
    }, 2000);
  }).catch((err) => {
    console.error('Could not copy text: ', err);
  });
};

const formatHistoryDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

async function fetchProfileHistory(page = 1) {
  if (!member.value?.userId) return;
  const safePage = Math.max(1, Number.parseInt(String(page || 1), 10) || 1);
  const safePageSize = Math.max(5, Number.parseInt(String(profileHistoryPagination.value.pageSize || 25), 10) || 25);

  historyLoading.value = true;
  try {
    const query = new URLSearchParams({
      page: String(safePage),
      pageSize: String(safePageSize),
    });
    const response = await fetch(
      `${apiProxyUrl}/api/members/${member.value.userId}/profile-history?${query.toString()}`,
      { credentials: 'include' }
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.success) {
      throw new Error(String(payload?.message || 'Failed loading profile history.'));
    }

    profileHistoryRows.value = Array.isArray(payload?.entries) ? payload.entries : [];
    associatedEmails.value = Array.isArray(payload?.associatedEmails)
      ? payload.associatedEmails.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [];
    profileHistoryPagination.value = {
      page: Number(payload?.pagination?.page || safePage),
      pageSize: Number(payload?.pagination?.pageSize || safePageSize),
      totalCount: Number(payload?.pagination?.totalCount || 0),
      totalPages: Number(payload?.pagination?.totalPages || 1),
      hasNextPage: Boolean(payload?.pagination?.hasNextPage),
      hasPreviousPage: Boolean(payload?.pagination?.hasPreviousPage),
    };
  } catch (error) {
    console.error('Error fetching profile history:', error);
    profileHistoryRows.value = [];
    associatedEmails.value = associatedEmails.value.length ? associatedEmails.value : [];
  } finally {
    historyLoading.value = false;
  }
}

async function fetchAssignedTags(memberUserId) {
  const userId = Number(memberUserId || 0);
  if (!Number.isFinite(userId) || userId <= 0) {
    assignedTags.value = [];
    return;
  }

  try {
    const response = await fetch(
      `${apiProxyUrl}/api/creator-tags/assigned?userId=${encodeURIComponent(String(userId))}&userType=member`,
      { credentials: 'include' },
    );
    const payload = await response.json().catch(() => ({}));

    if (response.ok && payload?.success && Array.isArray(payload?.assignments)) {
      assignedTags.value = payload.assignments
        .map((assignment) => ({
          userTagId: assignment?.userTagId || null,
          tagId: assignment?.tagId || null,
          name: String(assignment?.name || '').trim(),
        }))
        .filter((tag) => !!tag.name);
      return;
    }

    assignedTags.value = [];
  } catch (_error) {
    assignedTags.value = [];
  }
}

async function fetchMemberData() {
  try {
    console.log(`Fetching data for memberId: ${props.memberId}`);
    const response = await fetch(`${apiProxyUrl}/api/members/${props.memberId}`, { credentials: 'include' });
    const data = await response.json();

    if (data.success && data.member) {
      const normalizedProfilePic = normalizeProfilePicForSave(data.member.profile_pic);
      member.value = {
        userId: data.member.user_id || null,
        name: `${data.member.first_name || ''} ${data.member.last_name || ''}`.trim(),
        firstName: data.member.first_name || '',
        lastName: data.member.last_name || '',
        alias: data.member.alias || '',
        handle: data.member.handle || '',
        email: data.member.email || '',
        phone: data.member.phone || '',
        connectedCreatorStripeCustomerId: data.member.connected_creator_stripe_customer_id || '',
        platformaPaymentsStripeCustomerId: data.member.platforma_payments_stripe_customer_id || '',
        profilePic: normalizedProfilePic || defaultProfilePic,
        subscriptionStatus: data.member.subscription_status || data.member.status || 'Active',
        subscriptionPlan: data.member.subscription_plan_name || 'Basic',
        subscribeDate: data.member.created_at ? new Date(data.member.created_at).toLocaleDateString() : 'N/A',
        subscriptionDate: data.member.created_at ? new Date(data.member.created_at) : null,
      };

      form.value = {
        firstName: member.value.firstName,
        lastName: member.value.lastName,
        alias: member.value.alias || '',
        handle: member.value.handle || '@',
        email: member.value.email,
        phone: member.value.phone || '',
        connectedCreatorStripeCustomerId: member.value.connectedCreatorStripeCustomerId || '',
        platformaPaymentsStripeCustomerId: member.value.platformaPaymentsStripeCustomerId || '',
        profilePicUrl: normalizedProfilePic,
      };

      await fetchAssignedTags(member.value.userId);
      await fetchProfileHistory(1);
      passwordResetError.value = '';
      passwordResetSent.value = false;
      showPasswordResetConfirm.value = false;
      passwordResetConfirmChecked.value = false;

      const presenceId = member.value.userId;
      if (presenceId) {
        websocketStore.send({
          type: 'creator',
          event: 'presence:subscribeMembers',
          payload: {
            memberIds: [presenceId],
          },
        });
      }
    } else {
      member.value = { name: 'Unknown', email: 'Not available', profilePic: defaultProfilePic };
      assignedTags.value = [];
      associatedEmails.value = [];
      profileHistoryRows.value = [];
      profileHistoryPagination.value = {
        page: 1,
        pageSize: Number(profileHistoryPagination.value.pageSize || 25) || 25,
        totalCount: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }
  } catch (error) {
    console.error('Error fetching creator:', error);
    assignedTags.value = [];
    associatedEmails.value = [];
    profileHistoryRows.value = [];
  }
}

const triggerAvatarPicker = () => {
  if (isUploadingAvatar.value) return;
  avatarInputRef.value?.click?.();
};

const persistAvatarChange = async (profilePicUrl) => {
  if (!member.value?.userId) return;
  const response = await fetch(`${apiProxyUrl}/api/members/${member.value.userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ profilePicUrl: String(profilePicUrl || '').trim() }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.success) {
    throw new Error(String(payload?.message || 'Failed saving avatar.'));
  }
};

const handleAvatarSelected = async (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;

  isUploadingAvatar.value = true;
  errorMessage.value = '';
  try {
    const sanitizedFilename = encodeURIComponent(String(file.name || 'member-avatar').replace(/[^a-zA-Z0-9._-]/g, '_'));
    const signedResponse = await fetch(`${apiProxyUrl}/api/uploads/generate-signed-url`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: sanitizedFilename,
        contentType: file.type || 'image/png',
        imageType: 'creator_member_profile_avatar',
      }),
    });
    const signedPayload = await signedResponse.json().catch(() => ({}));
    if (!signedResponse.ok || !signedPayload?.signedUrl || !signedPayload?.permanentUrl) {
      throw new Error(String(signedPayload?.message || 'Unable to upload avatar right now.'));
    }

    const uploadResponse = await fetch(String(signedPayload.signedUrl), {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/png' },
      body: file,
    });
    if (!uploadResponse.ok) throw new Error('Failed uploading avatar image.');

    const permanentUrl = String(signedPayload.permanentUrl || '').trim();
    form.value.profilePicUrl = permanentUrl;
    if (member.value) {
      member.value.profilePic = permanentUrl || defaultProfilePic;
    }

    await persistAvatarChange(permanentUrl);
    await fetchProfileHistory(1);
    showSuccess('Profile photo updated successfully!');
  } catch (error) {
    errorMessage.value = String(error?.message || 'Avatar upload failed.');
  } finally {
    isUploadingAvatar.value = false;
    if (avatarInputRef.value) avatarInputRef.value.value = '';
  }
};

const saveProfile = async () => {
  saving.value = true;
  try {
    errorMessage.value = '';
    if (!member.value?.userId) {
      errorMessage.value = 'No user ID found for updating the profile.';
      return;
    }

    const response = await fetch(`${apiProxyUrl}/api/members/${member.value.userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        email: form.value.email,
        phone: form.value.phone,
        connectedCreatorStripeCustomerId: form.value.connectedCreatorStripeCustomerId,
        platformaPaymentsStripeCustomerId: form.value.platformaPaymentsStripeCustomerId,
        alias: form.value.alias,
        handle: String(form.value.handle || '').trim() === '@' ? '' : form.value.handle,
        profilePicUrl: normalizeProfilePicForSave(form.value.profilePicUrl),
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (response.ok && data.success) {
      isEditing.value = false;
      await fetchMemberData();
      showSuccess('Profile changes saved successfully!');
    } else {
      errorMessage.value = data.message || 'An error occurred while saving the profile. Please try again.';
    }
  } catch (error) {
    console.error('Error saving profile:', error);
    errorMessage.value = 'An unexpected error occurred. Please close the menu and try again.';
  } finally {
    saving.value = false;
  }
};

const cancelEdit = () => {
  isEditing.value = false;
  errorMessage.value = '';
  saving.value = false;

  form.value = {
    firstName: member.value?.firstName || '',
    lastName: member.value?.lastName || '',
    alias: member.value?.alias || '',
    handle: member.value?.handle || '@',
    email: member.value?.email || '',
    phone: member.value?.phone || '',
    connectedCreatorStripeCustomerId: member.value?.connectedCreatorStripeCustomerId || '',
    platformaPaymentsStripeCustomerId: member.value?.platformaPaymentsStripeCustomerId || '',
    profilePicUrl: normalizeProfilePicForSave(member.value?.profilePic),
  };
};

const toggleUserMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const openEditMode = () => {
  isEditing.value = true;
  menuOpen.value = false;
};

const handleUserAction = (action) => {
  menuOpen.value = false;
  if (action !== 'delete') return;
};

const handleClickOutside = (event) => {
  if (!event.target.closest('.user-dropdown')) {
    menuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  websocketStore.connect();
  fetchMemberData();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
